import { z } from 'zod';
import type { FeatureName } from './featureNames';
import type { DateString, Param } from './types';

// What the generated helpers' schemas are built from. A schema's input is the
// domain's camelCase names and its output the wire's, as a write's payload is
// under ADR 0019; these are the value schemas, the parameters every route
// shares, and the query of a route that reads none of its own.
//
// None of them refuses a value. A helper runs while a component renders, and a
// value that reaches it wrongly -- a NaN an extractor substituted, a boolean
// from a caller no type checks -- would cost the render or the request if it
// threw here, where `format` writes it and Rails copes. The type is the check,
// made at the call site; the schema is the rename.
function unchecked<T>() {
  return z.custom<T>(() => true).optional();
}

// Anything `QueryParams` admits. `null` leaves the parameter out, as it does on
// a route nobody has declared.
export const queryValue = unchecked<Param | Array<Param> | null>();

// A month that substituted where it was read, and reported there, is still a
// string, and nothing re-parses it here.
export const month = unchecked<DateString<'YYYY-MM'> | null>();

// One of a route's literals, named by the type argument.
export function oneOf<Literal extends string>() {
  return unchecked<Literal | null>();
}

// The features `ApplicationController#feature_enabled?` reports off. It splits
// one comma-separated string, so a list goes out joined: an array would arrive
// as `disable_features[]`, which it cannot split.
const featureList = unchecked<Array<FeatureName> | null>().transform(
  features => (Array.isArray(features) ? features.join(',') : features)
);

// Read by `ApplicationController`'s callback chain rather than by any single
// action: `auth` signs in the holder of a token, and `ref` and
// `disable_features` are read beside it. Every route takes them.
export const globalQuery = {
  auth: unchecked<Param | null>(),
  ref: unchecked<Param | null>(),
  disableFeatures: featureList
};

// A schema built the first time it is asked for, so importing `routes` builds
// no route's own: a page pays only for the helpers it calls.
export function lazy<Schema>(build: () => Schema): () => Schema {
  let built: Schema | undefined;
  return () => (built ??= build());
}

// A path parameter's value, in a route whose segments are renamed. The route's
// schema requires the key, and refuses one left out under the name a caller
// writes. The value itself is not checked: `format` refuses one it cannot use,
// under the name Rails gives the segment.
export const segment = z.custom<Param>(() => true);

// The query of a route with no entry in config/js_routes/query_params.yml: its
// action reads none of its own, so the type admits only the parameters every
// route takes.
const globalOnly = z.object(globalQuery);

export type GlobalQuery = z.input<typeof globalOnly>;

// What runs for such a route, and for a declared one that renames no key of its
// own: `disableFeatures` goes out under the name Rails reads, and every other
// key as written. A key the type does not admit came from a caller no type
// checks, and `catchall` passes it on, typed as the query value it has to be
// to reach `format`. It is spread last, so one that caller wrote under the name
// Rails reads goes out as written rather than as the `undefined` of the
// camelCase key it left out.
export const globalWire = globalOnly
  .catchall(queryValue)
  .transform(({ disableFeatures, ...query }) => ({
    disable_features: disableFeatures,
    ...query
  }));
